import { jwtDecode } from "jwt-decode";
import { getDeviceKeyPersistence } from "plg/auth/deviceKey";

export const PLG_ACCESS_TOKEN_KEY = "plgAccessToken";
export const PLG_REFRESH_TOKEN_KEY = "plgRefreshToken";
export const PLG_PROFILE_KEY = "plgProfile";

export interface PlgTokens {
  accessToken: string;
  refreshToken: string;
}

export interface PlgProfile {
  plgCustomerId: string;
  email: string;
  companyName: string;
  isVerified: boolean;
  permissions: string[];
  /** From the token: false until the last setup step is done (the app sends the customer back to setup). Missing = done. */
  setupComplete?: boolean;
  firstName?: string;
  lastName?: string;
  state?: string;
  industry?: string;
  size?: string;
  /** From /plg/me (not in the token): for analytics account traits. */
  emailType?: "personal" | "business";
  tier?: "blocked" | "full" | "redacted";
}

interface PlgClaims {
  typ?: string;
  plgCustomerId?: string;
  email?: string;
  companyName?: string;
  isVerified?: boolean;
  permissions?: string[];
  setupComplete?: boolean;
}

// Tokens are bound to the device key (v2.2). A key kept only in this tab's memory can't sign for
// another tab or a reload, so its tokens stay in sessionStorage instead of shared localStorage.
const stores = (): Storage[] => {
  const out: Storage[] = [];
  try {
    out.push(localStorage);
  } catch {
    // unavailable
  }
  try {
    out.push(sessionStorage);
  } catch {
    // unavailable
  }
  return out;
};

const read = (key: string): string | null => {
  for (const s of stores()) {
    try {
      const v = s.getItem(key);
      if (v !== null) return v;
    } catch {
      // try the next one
    }
  }
  return null;
};

const keyIsMemoryOnly = (): boolean => {
  try {
    return getDeviceKeyPersistence() === "memory";
  } catch {
    return false;
  }
};

const target = (): Storage | null => {
  try {
    return keyIsMemoryOnly() ? sessionStorage : localStorage;
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  const to = target();
  for (const s of stores()) {
    try {
      if (value === null || s !== to) s.removeItem(key);
      else s.setItem(key, value);
    } catch {
      // storage unavailable — the session lives only for this page load
    }
  }
};

export const getPlgAccessToken = () => read(PLG_ACCESS_TOKEN_KEY);
export const getPlgRefreshToken = () => read(PLG_REFRESH_TOKEN_KEY);

export function getPlgProfile(): PlgProfile | null {
  try {
    return JSON.parse(read(PLG_PROFILE_KEY) || "null");
  } catch {
    return null;
  }
}

export function updatePlgProfile(patch: Partial<PlgProfile>): void {
  const current = getPlgProfile();
  if (!current) return;
  write(PLG_PROFILE_KEY, JSON.stringify({ ...current, ...patch }));
}

/** Stores a PLG token pair. Returns null (and stores nothing) when the access token is not a PLG token. */
export function setPlgTokens(tokens: PlgTokens): PlgProfile | null {
  let claims: PlgClaims;
  try {
    claims = jwtDecode<PlgClaims>(tokens.accessToken);
  } catch {
    return null;
  }
  if (claims.typ !== "plg" || !claims.plgCustomerId) return null;
  const previous = getPlgProfile();
  const profile: PlgProfile = {
    ...(previous?.plgCustomerId === claims.plgCustomerId ? previous : {}),
    plgCustomerId: claims.plgCustomerId,
    email: claims.email || "",
    companyName: claims.companyName || "",
    isVerified: claims.isVerified === true,
    permissions: Array.isArray(claims.permissions) ? claims.permissions : [],
    // A token from before setup was saved step by step has no claim: that account finished setup.
    setupComplete: claims.setupComplete !== false,
  };
  write(PLG_ACCESS_TOKEN_KEY, tokens.accessToken);
  write(PLG_REFRESH_TOKEN_KEY, tokens.refreshToken);
  write(PLG_PROFILE_KEY, JSON.stringify(profile));
  return profile;
}

export function clearPlgSession(): void {
  write(PLG_ACCESS_TOKEN_KEY, null);
  write(PLG_REFRESH_TOKEN_KEY, null);
  write(PLG_PROFILE_KEY, null);
}

export const hasPlgSession = () => !!(getPlgAccessToken() || getPlgRefreshToken()) && !!getPlgProfile();

/** Signed in, with setup steps still to do: the app sends the customer back to them. */
export const isPlgSetupOpen = () => hasPlgSession() && getPlgProfile()?.setupComplete === false;
